export type ApiErrorCode =
  | 'network'
  | 'timeout'
  | 'offline'
  | 'unauthorized'
  | 'forbidden'
  | 'not_found'
  | 'validation'
  | 'server'
  | 'unknown';

export interface ApiErrorInit {
  message: string;
  code: ApiErrorCode;
  status: number | null;
  url: string;
  method: string;
  body?: unknown;
  cause?: unknown;
}

/**
 * Typed error envelope every request in this package rejects with. Callers
 * (web or native) can branch on `code`/`status` without knowing whether the
 * transport is axios or fetch underneath.
 */
export class ApiError extends Error {
  readonly code: ApiErrorCode;
  readonly status: number | null;
  readonly url: string;
  readonly method: string;
  readonly body: unknown;
  readonly cause?: unknown;

  constructor(init: ApiErrorInit) {
    super(init.message);
    this.name = 'ApiError';
    this.code = init.code;
    this.status = init.status;
    this.url = init.url;
    this.method = init.method;
    this.body = init.body;
    this.cause = init.cause;
  }

  /**
   * axios-error-compatible view (`err.response.status` / `err.response.data`).
   * Several already-shipped web-ide call sites branch on this shape (e.g.
   * `PortalCheckout.tsx`'s `err.response?.data?.error`,
   * `PortalCertOverview.tsx`'s `err.response?.status`, the signup-gate
   * detector in `useUserContext.ts`) because the previous transport was
   * axios. Exposing the same shape here means those call sites keep working
   * unmodified when a module is migrated onto this client — this is a
   * compatibility accessor, not a dependency on axios.
   */
  get response(): { status: number; data: unknown } | undefined {
    return this.status === null ? undefined : { status: this.status, data: this.body };
  }
}

export function codeForStatus(status: number): ApiErrorCode {
  if (status === 401) return 'unauthorized';
  if (status === 403) return 'forbidden';
  if (status === 404) return 'not_found';
  if (status === 422 || status === 400) return 'validation';
  if (status >= 500) return 'server';
  return 'unknown';
}

export function isApiError(value: unknown): value is ApiError {
  return value instanceof ApiError;
}
